import React from 'react';
import { Alert, Box, Typography, Chip } from '@mui/material';
import { AccessTime, CheckCircle, Warning, Block } from '@mui/icons-material';
import { useRegistrationStatus, getDeadlineMessage } from '../hooks/useRegistrationStatus';

interface RegistrationStatusAlertProps {
  competitionId: string;
  showDetails?: boolean;
}

const RegistrationStatusAlert: React.FC<RegistrationStatusAlertProps> = ({ 
  competitionId, 
  showDetails = true 
}) => {
  const registrationStatus = useRegistrationStatus(competitionId);

  if (registrationStatus.loading) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        {getDeadlineMessage(registrationStatus)}
      </Alert>
    );
  }

  if (registrationStatus.error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {registrationStatus.error}
      </Alert>
    );
  }

  const getAlertProps = () => {
    switch (registrationStatus.status) {
      case 'not_yet_open':
        return {
          severity: 'info' as const,
          icon: <AccessTime />,
          title: 'Meldung noch nicht geöffnet'
        };
      
      case 'open':
        return {
          severity: 'success' as const,
          icon: <CheckCircle />,
          title: 'Meldung geöffnet'
        };
      
      case 'extended':
        return {
          severity: 'warning' as const,
          icon: <AccessTime />,
          title: 'Meldung verlängert'
        };
      
      case 'closed':
        return {
          severity: 'error' as const,
          icon: <Block />,
          title: 'Meldung geschlossen'
        };
      
      default:
        return {
          severity: 'info' as const,
          icon: <Warning />,
          title: 'Status unbekannt'
        };
    }
  };

  const alertProps = getAlertProps();

  return (
    <Alert 
      severity={alertProps.severity} 
      icon={alertProps.icon}
      sx={{ mb: 2 }}
    >
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          {alertProps.title}
        </Typography>
        
        <Typography variant="body2">
          {getDeadlineMessage(registrationStatus)}
        </Typography>

        {showDetails && registrationStatus.isExtended && registrationStatus.extension && (
          <Box sx={{ mt: 1 }}>
            <Chip 
              label="Verlängert" 
              color="warning" 
              size="small" 
              icon={<AccessTime />}
              sx={{ mr: 1 }}
            />
            {registrationStatus.extension.reason && (
              <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                Grund: {registrationStatus.extension.reason}
              </Typography>
            )}
          </Box>
        )}

        {!registrationStatus.isOpen && (
          <Typography variant="caption" display="block" sx={{ mt: 1, fontStyle: 'italic' }}>
            Änderungen an der Meldung sind derzeit nicht möglich.
          </Typography>
        )}
      </Box>
    </Alert>
  );
};

export default RegistrationStatusAlert;